import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
];

export function LanguageSwitcher() {
  const { i18n } = useTranslation();

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = languageCode;
  };

  const resolvedLanguage = i18n.resolvedLanguage || i18n.language;
  const normalizedLanguageCode = (() => {
    if (!resolvedLanguage) {
      return languages[0].code;
    }

    const exactMatch = languages.find((lang) => lang.code === resolvedLanguage);
    if (exactMatch) {
      return exactMatch.code;
    }

    const baseCode = resolvedLanguage.split('-')[0];
    const baseMatch = languages.find((lang) => lang.code === baseCode);
    return baseMatch ? baseMatch.code : languages[0].code;
  })();

  const currentLanguage =
    languages.find((lang) => lang.code === normalizedLanguageCode) || languages[0];

  return (
    <div className="space-y-2">
      <Label htmlFor="language" className="flex items-center gap-2">
        <Globe className="h-4 w-4" />
        Language / Langue / Sprache
      </Label>
      <Select value={normalizedLanguageCode} onValueChange={handleLanguageChange}>
        <SelectTrigger id="language" data-testid="language-switcher">
          <SelectValue>
            {currentLanguage.flag} {currentLanguage.name}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {languages.map((language) => (
            <SelectItem
              key={language.code}
              value={language.code}
              data-testid={`language-option-${language.code}`}
            >
              {language.flag} {language.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
